import React, { useEffect, useRef, useState } from 'react';
import { Form, Button, Container, Row, Col, InputGroup } from 'react-bootstrap';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import styles from '../../chat/components/AccountDeletionForm.module.scss';

import StopWatch from '../../chat/components/StopWatch';
import AlertModal from '@components/modal/AlertModal';
import {
  useSmsDeleteCertificationMutation,
  useSmsDeleteRequestMutation,
  useUserCountryCodeQuery
} from '@hooks/api/chatUserApi';
import { useDeleteGamesUserDataMutation } from '@hooks/api/gamesUserApi';

const GamesAccountDeletionForm: React.FC = () => {
  const [uuidData, setUuidData] = useState<string>(uuidv4()); // 처음
  const [countryCode, setCountryCode] = useState<string>('');
  const [verifyCode, setVerifyCode] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [formatPhoneNumber, setFormatPhoneNumber] = useState<number>(0);
  const [userId, setUserId] = useState<string>('');
  const [isSmsVerificationSent, setSmsVerificationSent] = useState<boolean>(false);
  const [isSmsBtnDisabled, setSmsBtnDisabled] = useState<boolean>(false);
  const [resetSignal, setResetSignal] = useState(false);
  const [sendErrorMessage, setSendErrorMessage] = useState<string>('');
  const [focusField, setFocusField] = useState<string>('userId');
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    txt: '',
    confirmBtnVisible: false
  });

  const userIdRef = useRef<HTMLInputElement>(null);
  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const verifyCodeRef = useRef<HTMLInputElement>(null);

  const { data: userCountryCodeData } = useUserCountryCodeQuery();
  const { mutate: smsDeleteRequestMutate } = useSmsDeleteRequestMutation();
  const { mutate: smsCertificationMutate } = useSmsDeleteCertificationMutation();
  const { mutate: userGamesDeleteMutate } = useDeleteGamesUserDataMutation();

  const handleCountryCodeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryCode(event.target.value);
  };

  const handleUserIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserId(event.target.value);
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
  };
  const handleVerifyCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerifyCode(event.target.value);
  };

  const handleSendSmsVerification = (event: React.FormEvent<HTMLFormElement>, input: string, countryCode: string) => {
    event.preventDefault();
    setSendErrorMessage('');
    if (userId.length < 3) {
      setAlertModal((prev) => ({
        ...prev,
        isOpen: true,
        txt: 'The ID field is required.'
      }));
      setFocusField('userId');
      return;
    }
    const completePhoneNumber = processPhoneNumber(input, countryCode);

    if (completePhoneNumber) {
      setResetSignal(true);
      setFormatPhoneNumber(Number(completePhoneNumber));

      //1. Send SMS Verification API 호출용
      smsDeleteRequestMutate(
        {
          request: {
            grantType: 'delete',
            phoneNumber: Number(completePhoneNumber),
            userId
          },
          headers: {
            language: 'en',
            uuid: uuidData
          }
        },
        {
          onSuccess: () => {
            setSmsVerificationSent(true);
            setSmsBtnDisabled(true);
            setFocusField('verifyCode');
            setAlertModal((prev) => ({
              ...prev,
              isOpen: true,
              confirmBtnVisible: false,
              txt: 'The SMS authentication number has been sent.'
            }));
          },
          onError: (error) => {
            setAlertModal((prev) => ({
              ...prev,
              isOpen: true,
              confirmBtnVisible: false,
              txt: error.response?.data.message as string
            }));
          }
        }
      );
    }
  };

  // 2. verify Code 입력으로 최종 delete 확인 submit 함수
  const handleSendVerifyCode = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (verifyCode.length === 6) {
      smsCertificationMutate(
        {
          request: {
            grantType: 'delete',
            phoneNumber: formatPhoneNumber,
            certNumber: Number(verifyCode),
            userId
          },
          headers: {
            language: 'en',
            uuid: uuidData
          }
        },
        {
          onSuccess: () => {
            setAlertModal((prev) => ({
              ...prev,
              isOpen: true,
              confirmBtnVisible: true,
              txt: 'verification is complete. Do you want to proceed with data deletion?'
            }));
          },
          onError: (error) => {
            setSmsBtnDisabled(false);
            setPhoneNumber('');
            setUserId('');
            setVerifyCode('');
            setCountryCode('855');
            setUuidData(uuidv4());
            setFocusField('userId');
            setAlertModal((prev) => ({
              ...prev,
              isOpen: true,
              txt: error.response?.data.message as string
            }));
          }
        }
      );
    } else {
      setAlertModal((prev) => ({
        ...prev,
        isOpen: true,
        txt: 'Please enter a valid 6-digit verification code.'
      }));
      setFocusField('verifyCode');
    }

    setAlertModal((prev) => ({ ...prev, isOpen: true }));
  };

  const handleReset = () => {
    setSmsBtnDisabled(false);
    setResetSignal(false);
    setSendErrorMessage('Verification code has expired. Please request a new code.');
  };

  /* 핸드폰 번호에 대한 정규식 & 에러 처리 함수 */
  const processPhoneNumber = (input: string, countryCode: string) => {
    // (한영자+ 특수문자 제외) 숫자만 남기기
    const onlyNumbers = input.replace(/\D/g, '');

    if (countryCode === '') {
      setAlertModal((prev) => ({
        ...prev,
        isOpen: true,
        txt: 'Please select your country code.'
      }));
      setFocusField('phoneNumber');
      return;
    }

    // 11자리 숫자인 경우 맨 앞자리 '0'을 제외하고 10자리로 만들기
    if (onlyNumbers.length > 5 && onlyNumbers.startsWith('0')) {
      return countryCode + onlyNumbers.slice(1);
    } else if (onlyNumbers.length > 5) {
      return countryCode + onlyNumbers;
    } else {
      setAlertModal((prev) => ({
        ...prev,
        isOpen: true,
        txt: 'Please enter a valid phone number (6-20 digits, numbers only).'
      }));
      setFocusField('phoneNumber');
      return;
    }
  };

  // phoneNumber 다시 작성 시에 Send SMS Verification 버튼 재활성화
  useEffect(() => {
    setSmsBtnDisabled(false);
    setSmsVerificationSent(false);
    setResetSignal(false);
  }, [phoneNumber]);

  // 처음 국가 코드 API가 성공했을 때, 처음 보이는 캄보디아 국가 코드를 갖는다.
  useEffect(() => {
    if (userCountryCodeData) {
      setCountryCode('855');
    }
  }, [userCountryCodeData]);

  // 모달이 닫힐 때 포커스 설정
  useEffect(() => {
    if (!alertModal.isOpen) {
      if (focusField === 'userId' && userIdRef.current) {
        userIdRef.current.focus();
      } else if (focusField === 'phoneNumber' && phoneNumberRef.current) {
        phoneNumberRef.current.focus();
      } else if (focusField === 'verifyCode' && verifyCodeRef.current) {
        verifyCodeRef.current.focus();
      }
    }
  }, [alertModal.isOpen, focusField]);

  return (
    <>
      {alertModal.isOpen && (
        <AlertModal
          isOpen={alertModal.isOpen}
          confirmBtn={
            alertModal.confirmBtnVisible
              ? {
                  // 3. 회원 삭제 API
                  onClose: () => {
                    userGamesDeleteMutate(
                      {
                        request: {
                          phoneNumber: formatPhoneNumber,
                          userId
                        },
                        headers: {
                          language: 'en',
                          uuid: uuidData
                        }
                      },
                      {
                        onSuccess: () => {
                          setAlertModal((prev) => ({
                            ...prev,
                            confirmBtnVisible: false,
                            txt: 'Your request has been successfully submitted.'
                          }));

                          setSmsVerificationSent(false);
                          setSmsBtnDisabled(false);
                          setPhoneNumber('');
                          setUserId('');
                          setVerifyCode('');
                          setCountryCode('855');
                          setUuidData(uuidv4());
                          setFocusField('userId');
                        },
                        onError: (error) => {
                          setSmsVerificationSent(false);
                          setSmsBtnDisabled(false);
                          setPhoneNumber('');
                          setUserId('');
                          setVerifyCode('');
                          setCountryCode('855');
                          setUuidData(uuidv4());
                          setFocusField('userId');
                          setAlertModal((prev) => ({
                            ...prev,
                            isOpen: true,
                            confirmBtnVisible: false,
                            txt: error.response?.data.message as string
                          }));
                        }
                      }
                    );
                  },
                  buttonName: 'Delete Data'
                }
              : undefined
          }
          closeBtn={{
            onClose: () => setAlertModal((prev) => ({ ...prev, isOpen: false })),
            buttonName: 'Close'
          }}
        >
          {alertModal.txt}
        </AlertModal>
      )}

      <Container className={classNames(styles.container, 'mt-3')} style={{ width: '90%' }}>
        <Row className="justify-content-md-center">
          <Col md="7" className="w-100 px-4">
            <Form
              className="d-flex flex-column align-items-center"
              onSubmit={(event) => handleSendSmsVerification(event, phoneNumber, countryCode)}
            >
              <Col
                xs={12}
                className={classNames(
                  'd-flex',
                  'flex-column',
                  'align-items-center',
                  'gap-2',
                  sendErrorMessage ? 'mb-3' : 'mb-2'
                )}
              >
                <div className="w-100">
                  <Form.Group controlId="exampleForm.ControlInput1" className="mb-3 w-100">
                    <Form.Label className={classNames(styles.bold_label)}>Enter your AngkorLife ID:</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="userId"
                      onChange={handleUserIdChange}
                      value={userId}
                      ref={userIdRef}
                      maxLength={30}
                    />
                  </Form.Group>
                  <Form.Group controlId="phoneNumber" className="mb-4">
                    <Form.Label className={classNames(styles.bold_label)}>Enter the phone number you used:</Form.Label>
                    <InputGroup>
                      <Col xs={4} className="p-0">
                        <InputGroup.Text className={styles.inputGroupText}>
                          <Form.Select
                            value={countryCode}
                            onChange={handleCountryCodeChange}
                            className={styles.formSelect}
                          >
                            {userCountryCodeData?.map((country) => (
                              <option key={country.name} value={country.phoneCode}>
                                {country.name} ({country.phoneCode})
                              </option>
                            ))}
                          </Form.Select>
                        </InputGroup.Text>
                      </Col>
                      <Col xs={8}>
                        <Form.Control
                          type="tel"
                          placeholder="000-1234-4567"
                          maxLength={20}
                          value={phoneNumber}
                          onChange={handlePhoneNumberChange}
                          className={styles.phoneNumberInput}
                          ref={phoneNumberRef}
                        />
                      </Col>
                    </InputGroup>
                  </Form.Group>
                </div>

                <div className={classNames(sendErrorMessage ? 'mb-2' : 'mb-4')}>
                  <Button
                    variant={sendErrorMessage ? 'danger' : isSmsBtnDisabled ? 'secondary' : 'primary'}
                    type="submit"
                    disabled={isSmsBtnDisabled}
                  >
                    {sendErrorMessage ? 'Resend Verification Code' : 'Send SMS Verification'}
                  </Button>
                </div>
                {sendErrorMessage && <div className={classNames(styles.sendErrorMessage)}>{sendErrorMessage}</div>}
              </Col>
            </Form>
            <Form onSubmit={handleSendVerifyCode}>
              {isSmsVerificationSent && (
                <Col xs={12} className="d-flex flex-column align-items-center">
                  <Form.Group controlId="exampleForm.ControlInput1" className="mb-4 w-100">
                    <Form.Label className={classNames(styles.bold_label)}>Enter your Verification code:</Form.Label>
                    <Form.Control
                      type="tel"
                      value={verifyCode}
                      onChange={handleVerifyCodeChange}
                      maxLength={6}
                      ref={verifyCodeRef}
                    />
                  </Form.Group>
                  <StopWatch initialTime={180} onReset={handleReset} resetSignal={resetSignal} />
                  <Button
                    variant="warning"
                    type="submit"
                    className={classNames(styles.btn_waring)}
                    disabled={sendErrorMessage !== ''}
                  >
                    Verify Code
                  </Button>
                </Col>
              )}
            </Form>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default GamesAccountDeletionForm;
